/**
 * React Query hooks for session, providers, identities and profile, plus the
 * mutations the account pages need. Query keys per spec §12.1; retry policy is
 * configured globally in `providers.tsx` (spec §11.1).
 */
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import {
  getIdentities,
  getProviders,
  getSession,
  linkStart,
  logout,
  logoutAll,
  unlinkIdentity,
} from '../api/authApi';
import { clearCsrfToken, setCsrfToken } from '../api/csrf';
import type {
  CompleteOnboardingRequest,
  IdentitiesResponse,
  LinkStartRequest,
  LogoutAllResponse,
  ProviderId,
  ProvidersResponse,
  SessionResponse,
  UpdateProfileRequest,
  UserProfile,
} from '../api/apiSchemas';
import { completeOnboarding, getMe, updateProfile } from '../api/userApi';
import { queryKeys } from '../lib/queryKeys';

async function fetchSession(signal: AbortSignal): Promise<SessionResponse> {
  const result = await getSession(signal);
  if (result.authenticated) setCsrfToken(result.csrfToken);
  else clearCsrfToken();
  return result;
}

export function useSession(): UseQueryResult<SessionResponse> {
  return useQuery({
    queryKey: queryKeys.session,
    queryFn: ({ signal }) => fetchSession(signal),
    staleTime: 15_000,
    refetchOnWindowFocus: false,
  });
}

export function useProviders(client: string | null): UseQueryResult<ProvidersResponse> {
  return useQuery({
    queryKey: queryKeys.providers(client),
    queryFn: ({ signal }) => getProviders(client, signal),
    staleTime: 60_000,
  });
}

export function useIdentities(enabled: boolean): UseQueryResult<IdentitiesResponse> {
  return useQuery({
    queryKey: queryKeys.identities,
    queryFn: ({ signal }) => getIdentities(signal),
    enabled,
  });
}

export function useMe(enabled: boolean): UseQueryResult<UserProfile> {
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: ({ signal }) => getMe(signal),
    enabled,
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateProfileRequest) => updateProfile(body),
    onSuccess: (profile) => {
      queryClient.setQueryData(queryKeys.me, profile);
      void queryClient.invalidateQueries({ queryKey: queryKeys.session });
    },
  });
}

export function useCompleteOnboarding() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CompleteOnboardingRequest) => completeOnboarding(body),
    onSuccess: ({ user }) => {
      queryClient.setQueryData(queryKeys.me, user);
      void queryClient.invalidateQueries({ queryKey: queryKeys.session });
    },
  });
}

export function useLinkStart() {
  return useMutation({
    mutationFn: (input: { provider: ProviderId; body: LinkStartRequest }) =>
      linkStart(input.provider, input.body),
  });
}

export function useUnlinkIdentity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (provider: ProviderId) => unlinkIdentity(provider),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.identities });
      void queryClient.invalidateQueries({ queryKey: queryKeys.session });
    },
  });
}

export function useLogout() {
  return useMutation({ mutationFn: () => logout() });
}

export function useLogoutAll() {
  return useMutation<LogoutAllResponse>({ mutationFn: () => logoutAll() });
}
